import React, { useEffect, useState } from "react";
import cn from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import style from "./ExecutorTab.module.scss";
import { Driver } from "../../../../../services";
import UserProfileIcon from "../../../../shared/icons/UserProfileIcon";
import TransportCoveredIconNew from "../../../../shared/icons/TransportCoveredIconNew";

const EMPTY_VALUE = "-";

const CompanyIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 21h18M4 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M14 21V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v12M8 7h2M8 11h2M8 15h2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ExecutorTab = ({ order }) => {
  const { t } = useTranslation();

  const [driversData, setDriversData] = useState({});

  const drivers = order?.drivers || [];
  const driversKey = drivers.map((d) => d.driverId).join(",");
  const transportCompanyName = order?.expeditorName;

  useEffect(() => {
    if (!drivers.length) {
      setDriversData({});
      return;
    }

    drivers.forEach(({ driverId, vehicleId }) => {
      setDriversData((prev) => ({
        ...prev,
        [driverId]: prev[driverId] || { info: null, vehicleInfo: null },
      }));

      Driver.getDriverInfo(driverId)
        .then((r) => {
          if (r.status === 200) {
            setDriversData((prev) => ({
              ...prev,
              [driverId]: { ...prev[driverId], info: r.data || {} },
            }));
          }
        })
        .catch(() => {});

      if (vehicleId) {
        Driver.getVehicleInfo(vehicleId)
          .then((r) => {
            if (r.status === 200) {
              setDriversData((prev) => ({
                ...prev,
                [driverId]: { ...prev[driverId], vehicleInfo: r.data || {} },
              }));
            }
          })
          .catch(() => {});
      }
    });
  }, [driversKey]);

  const hasDrivers = drivers.length > 0;

  if (!hasDrivers && !transportCompanyName) {
    return <div className={style.empty}>{t("orderInfo.ordersDriverInfo.noExecutor")}</div>;
  }

  return (
    <div className={style.wrapper}>
      {transportCompanyName && (
        <div className={style.card}>
          <div className={cn(style.topbar)}>
            <div className={style.company_icon}>
              <CompanyIcon />
            </div>
            <div className={style.topbar_info}>
              <span className={style.topbar_label}>{t("orderInfoClient.executorTab.transportCompanyLabel")}</span>
              <span className={style.topbar_name}>{transportCompanyName}</span>
            </div>
          </div>
          <div className={style.fields_row}>
            <div className={style.field}>
              <span className={style.field_label}>{t("orderInfoClient.executorTab.companyBinLabel")}</span>
              <span className={style.field_value}>{order?.expeditorBIN || EMPTY_VALUE}</span>
            </div>
          </div>
        </div>
      )}

      {drivers.map(({ driverId, vehicleId, comment, shipmentTransferDate }) => {
        const { info, vehicleInfo } = driversData[driverId] || { info: null, vehicleInfo: null };
        const driverName = info
          ? [info.surname, info.name, info.patronymic].filter(Boolean).join(" ")
          : EMPTY_VALUE;

        return (
          <React.Fragment key={driverId}>
            <div>
              {(comment || shipmentTransferDate) && (
                <div className={style.route_bar}>
                  <span className={style.route_text}>{comment || EMPTY_VALUE}</span>
                  {shipmentTransferDate && (
                    <span className={style.route_date}>
                      {moment(shipmentTransferDate).format("DD.MM.YYYY")}
                    </span>
                  )}
                </div>
              )}

              <div className={cn(style.card, { [style.card_connected]: comment || shipmentTransferDate })}>
                <div className={style.topbar}>
                  <div className={style.avatar}>
                    <UserProfileIcon />
                  </div>
                  <div className={style.topbar_info}>
                    <span className={style.topbar_label}>{t("orderInfo.ordersDriverInfo.driverLabel")}</span>
                    <span className={style.topbar_name}>{driverName}</span>
                  </div>
                </div>
                <div className={style.fields_grid}>
                  <div className={style.field}>
                    <span className={style.field_label}>
                      {info?.iin
                        ? t("orderInfoExpeditor.executorTab.iinPassportLabel")
                        : t("orderInfoExpeditor.executorTab.passportNumber")}
                    </span>
                    <span className={style.field_value}>
                      {info?.iin ? `${info.iin} / ${info?.idNumber}` : info?.idNumber || EMPTY_VALUE}
                    </span>
                  </div>
                  <div className={style.field}>
                    <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.phoneLabel")}</span>
                    <span className={style.field_value}>{info?.phoneNumber ? `+${info.phoneNumber}` : EMPTY_VALUE}</span>
                  </div>
                  <div className={style.field}>
                    <span className={style.field_label}>{t("driversCreatePage.issuedBy.label")}</span>
                    <span className={style.field_value}>{info?.issuedBy || EMPTY_VALUE}</span>
                  </div>
                  <div className={style.field}>
                    <span className={style.field_label}>{t("driversCreatePage.issuedDate.label")}</span>
                    <span className={style.field_value}>
                      {info?.issuedDate ? moment(info.issuedDate).format("DD.MM.YYYY") : EMPTY_VALUE}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={style.card}>
              <div className={style.topbar}>
                <div className={style.transport_icon}>
                  <TransportCoveredIconNew />
                </div>
                <div className={style.topbar_info}>
                  <span className={style.topbar_label}>{t("orderInfo.ordersDriverInfo.transportLabel")}</span>
                  <span className={style.topbar_name}>{vehicleInfo?.vehicleTypeName || vehicleInfo?.carModel || EMPTY_VALUE}</span>
                </div>
              </div>
              <div className={style.fields_grid}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.carModelLabel")}</span>
                  <span className={style.field_value}>{vehicleInfo?.carModel || EMPTY_VALUE}</span>
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.carNumberLabel")}</span>
                  <span className={style.field_value}>{vehicleInfo?.carNumber || EMPTY_VALUE}</span>
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.trailerModelLabel")}</span>
                  <span className={style.field_value}>{vehicleInfo?.trailerModel || EMPTY_VALUE}</span>
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.trailerNumberLabel")}</span>
                  <span className={style.field_value}>{vehicleInfo?.trailerNumber || EMPTY_VALUE}</span>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ExecutorTab;
